import { VOCATION_NAMES, Vocation, type Vocation as VocationType } from '@game/protocol';

import {
  ApiError,
  createCharacter,
  login,
  register,
  requestPlaySession,
  type CharacterSummary,
} from '../net/api.ts';

/**
 * Telas de conta e de personagem (PRD §6).
 *
 * Em DOM, não em Phaser. A diferença não é gosto: campo de senha, autopreenchimento do
 * navegador, teclado de celular e leitor de tela funcionam de graça num `<input>` e
 * teriam que ser reimplementados um a um dentro do canvas. O canvas começa depois, e só
 * quando há um token na mão.
 */

export interface LoginOutcome {
  sessionToken: string;
  characterName: string;
  vocation: VocationType;
  /**
   * Credenciais da conta, que sobrevivem à entrada no jogo.
   *
   * O `sessionToken` é de uso único (PRD §6) e morre no primeiro frame do WebSocket, então
   * ele não serve para reabrir a conexão depois de uma queda. Estes dois servem: com eles
   * o cliente pede uma sessão nova ao Login Server sem mandar o jogador digitar a senha de
   * novo — que é o que o botão de reconectar precisa (§11.2, item 4).
   */
  accountToken: string;
  characterId: number;
  /**
   * Porta em que este personagem entra, quando o servidor roda um processo por região
   * (PRD §3.4). Ausente no modelo de gateway único, e aí vale a porta de configuração.
   */
  gamePort?: number;
}

/** Descrição curta de cada vocação, do §9.1 do PRD, para a tela de criação. */
const VOCATION_PITCH: Readonly<Record<VocationType, string>> = {
  [Vocation.Knight]: 'Tanque corpo a corpo. Absorve dano e segura a criatura.',
  [Vocation.Paladin]: 'Dano físico à distância. Ataca fora do alcance do inimigo.',
  [Vocation.Druid]: 'Suporte e controle. Cura o grupo e freia o inimigo.',
  [Vocation.Sorcerer]: 'Dano mágico em área. Elimina vários alvos de uma vez.',
};

export function showLoginScreen(root: HTMLElement): Promise<LoginOutcome> {
  return new Promise((resolve) => {
    const screen = new LoginScreen(root, resolve);
    screen.render();
  });
}

type Step = 'account' | 'characters' | 'create';

class LoginScreen {
  private readonly root: HTMLElement;
  private readonly done: (outcome: LoginOutcome) => void;

  private step: Step = 'account';
  private accountToken = '';
  private characters: CharacterSummary[] = [];
  private message = '';
  private busy = false;
  private chosenVocation: VocationType = Vocation.Knight;

  constructor(root: HTMLElement, done: (outcome: LoginOutcome) => void) {
    this.root = root;
    this.done = done;
  }

  render(): void {
    this.root.innerHTML = '';
    this.root.classList.add('visible');

    const panel = el('div', 'auth-panel');
    panel.append(el('h1', 'auth-title', 'Hexaria'));

    if (this.step === 'account') panel.append(this.accountForm());
    else if (this.step === 'characters') panel.append(this.characterList());
    else panel.append(this.createForm());

    if (this.message) {
      panel.append(el('p', 'auth-message', this.message));
    }

    this.root.append(panel);
  }

  // --- Conta ---------------------------------------------------------------

  private accountForm(): HTMLElement {
    const form = el('form', 'auth-form');

    const email = input('email', 'E-mail', 'email');
    const password = input('password', 'Senha', 'current-password');

    const enter = button('Entrar', 'primary');
    const create = button('Criar conta', 'ghost');

    form.append(
      label('E-mail', email),
      label('Senha', password),
      row(enter, create),
      el(
        'p',
        'auth-hint',
        'Uma conta guarda até cinco personagens. A senha é gravada com Argon2id.',
      ),
    );

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      void this.submitAccount(email.value, password.value, false);
    });

    create.addEventListener('click', (event) => {
      event.preventDefault();
      void this.submitAccount(email.value, password.value, true);
    });

    // Foco no primeiro campo: quem abre a página quer digitar, não procurar onde clicar.
    queueMicrotask(() => email.focus());
    return form;
  }

  private async submitAccount(
    email: string,
    password: string,
    isNew: boolean,
  ): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.message = isNew ? 'criando conta…' : 'entrando…';
    this.render();

    try {
      if (isNew) await register(email, password);

      const result = await login(email, password);
      this.accountToken = result.accountToken;
      this.characters = result.characters;
      this.message = '';
      // Conta nova nunca tem personagem: pular a lista vazia poupa um clique de todo
      // jogador que está entrando pela primeira vez.
      this.step = this.characters.length === 0 ? 'create' : 'characters';
    } catch (error) {
      this.message = error instanceof ApiError ? error.message : 'erro inesperado';
    } finally {
      this.busy = false;
      this.render();
    }
  }

  // --- Personagens ---------------------------------------------------------

  private characterList(): HTMLElement {
    const list = el('div', 'auth-form');
    list.append(el('h2', 'auth-subtitle', 'Escolha um personagem'));

    for (const character of this.characters) {
      const card = el('button', 'character-card');
      card.type = 'button';

      card.append(
        el('span', 'character-name', character.name),
        el(
          'span',
          'character-meta',
          `${VOCATION_NAMES[character.vocation]} · nível ${character.level}`,
        ),
      );

      card.addEventListener('click', () => void this.play(character));
      list.append(card);
    }

    const create = button('Criar personagem', 'ghost');
    create.addEventListener('click', () => {
      this.step = 'create';
      this.message = '';
      this.render();
    });

    list.append(create);
    return list;
  }

  private async play(character: CharacterSummary): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.message = `entrando com ${character.name}…`;
    this.render();

    try {
      const { sessionToken, gamePort } = await requestPlaySession(this.accountToken, character.id);

      this.root.classList.remove('visible');
      this.root.innerHTML = '';
      this.done({
        sessionToken,
        ...(gamePort === undefined ? {} : { gamePort }),
        characterName: character.name,
        vocation: character.vocation,
        accountToken: this.accountToken,
        characterId: character.id,
      });
    } catch (error) {
      this.message = error instanceof ApiError ? error.message : 'erro inesperado';
      this.busy = false;
      this.render();
    }
  }

  // --- Criação -------------------------------------------------------------

  private createForm(): HTMLElement {
    const form = el('form', 'auth-form');
    form.append(el('h2', 'auth-subtitle', 'Novo personagem'));

    const name = input('text', 'Nome', 'off');
    form.append(label('Nome', name));

    const choices = el('div', 'vocation-grid');

    for (const vocation of [
      Vocation.Knight,
      Vocation.Paladin,
      Vocation.Druid,
      Vocation.Sorcerer,
    ]) {
      const card = el('button', 'vocation-card');
      card.type = 'button';
      if (vocation === this.chosenVocation) card.classList.add('selected');

      card.append(
        el('span', 'vocation-name', VOCATION_NAMES[vocation]),
        el('span', 'vocation-pitch', VOCATION_PITCH[vocation]),
      );

      card.addEventListener('click', () => {
        this.chosenVocation = vocation;
        // Re-render em vez de mexer nas classes à mão: o estado escolhido mora numa
        // variável só, e a tela é sempre desenhada a partir dele.
        this.render();
        queueMicrotask(() => {
          const field = this.root.querySelector<HTMLInputElement>('input[type="text"]');
          if (field) field.value = name.value;
        });
      });

      choices.append(card);
    }

    form.append(
      choices,
      el(
        'p',
        'auth-hint',
        'A vocação é escolhida uma vez e não muda depois. Ela decide vida, mana, ' +
          'velocidade de ataque, quais skills sobem rápido e quais magias você conjura.',
      ),
      row(button('Criar e jogar', 'primary'), this.backButton()),
    );

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      void this.submitCharacter(name.value);
    });

    queueMicrotask(() => name.focus());
    return form;
  }

  private backButton(): HTMLButtonElement {
    const back = button('Voltar', 'ghost');
    back.addEventListener('click', (event) => {
      event.preventDefault();
      this.step = this.characters.length > 0 ? 'characters' : 'account';
      this.message = '';
      this.render();
    });
    return back;
  }

  private async submitCharacter(name: string): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.message = 'criando personagem…';
    this.render();

    try {
      const { character } = await createCharacter(
        this.accountToken,
        name,
        this.chosenVocation,
      );

      this.characters.push(character);
      this.busy = false;
      await this.play(character);
    } catch (error) {
      this.message = error instanceof ApiError ? error.message : 'erro inesperado';
      this.busy = false;
      this.render();
    }
  }
}

// --- Fábricas de elemento ---------------------------------------------------

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function input(
  type: string,
  placeholder: string,
  autocomplete: AutoFill,
): HTMLInputElement {
  const field = document.createElement('input');
  field.type = type;
  field.placeholder = placeholder;
  // O tipo é o do DOM, e não `string`, de propósito: `autocomplete` só aceita valores
  // de um vocabulário fechado, e um valor inventado é ignorado em silêncio pelo
  // navegador — a senha simplesmente não seria salva no gerenciador.
  field.autocomplete = autocomplete;
  field.required = true;
  return field;
}

function label(text: string, field: HTMLElement): HTMLLabelElement {
  const wrapper = document.createElement('label');
  wrapper.className = 'auth-label';
  wrapper.append(el('span', 'auth-label-text', text), field);
  return wrapper;
}

function button(text: string, variant: string): HTMLButtonElement {
  const node = document.createElement('button');
  node.className = `auth-button ${variant}`;
  node.textContent = text;
  return node;
}

function row(...children: HTMLElement[]): HTMLElement {
  const node = el('div', 'auth-row');
  node.append(...children);
  return node;
}
